import useLearningCenter from '~/app/hooks/use-learning-center'
import {
    areAllModulesComplete,
    areAllLevelsComplete
} from 'hec/builder/helpers/learningCenterHelpers'
import {
    trackItemCompleted,
    trackItemStarted,
    trackItemOpened,
    trackQuizAttempt
} from '~/app/utils/segment/learning-center/eventHandlers'
import {learningCenterEventNames} from '~/app/utils/segment/learning-center/constants'

const useLearningCenterMutation = () => {
    const {learningCenterData, getModuleByID, getLevelByID, getGoalByID, updateData} =
        useLearningCenter()

    /**
     * Pushes new goal to the updated learning center data object and returns the updated data.
     *
     * @param {string} goalID - goal ID
     * @returns {object} new learning center data
     */
    const openGoal = async (goalID, fromStartGoal = false) => {
        const newData = learningCenterData ? structuredClone(learningCenterData) : {}
        const sfccGoal = getGoalByID(goalID, learningCenterData)

        if (sfccGoal) return {success: true}
        const openTime = new Date().toISOString()

        if (!newData.goals) {
            newData.goals = []
        }

        newData.goals.push({
            id: goalID,
            open: openTime,
            levels: []
        })

        if (!newData?.activeGoal) {
            newData.activeGoal = goalID
        }

        trackItemOpened({
            eventType: learningCenterEventNames.goalProgressStatus,
            id: goalID,
            openedTimestamp: openTime
        })
        return fromStartGoal
            ? await new Promise((resolve) => {
                  resolve({
                      data: newData
                  })
              })
            : await updateData(newData)
    }

    /**
     * Pushes the completed lesson to the updated learning center data object and returns the updated data.
     *
     * @param {string} lessonID - lesson ID
     * @param {string} moduleID - module ID
     * @returns {object | Promise} A result object or a Promise that resolves with the result of the updateData method.
     */
    const completeLesson = (lessonID, nextLessonId, moduleID, updateGuestLearnCenterData) => {
        if (!lessonID) return
        let newData = structuredClone(learningCenterData)
        let goalID = lessonID.split('--')[0]
        const levelID = lessonID.split('--').slice(0, 2).join('--')
        const currentTime = new Date().toISOString()

        if (!newData || (newData.goals && newData.goals.length === 0)) {
            newData = {}

            if (!newData.goals) {
                newData.goals = []
            }

            newData.goals.push({
                id: goalID,
                open: currentTime,
                levels: []
            })

            if (!newData?.activeGoal) {
                newData.activeGoal = goalID
            }
        }

        const sfccGoal = getGoalByID(goalID, newData)
        if (!sfccGoal) return

        const levelExists = sfccGoal.levels.some((level) => level.id === levelID)
        if (!levelExists) {
            sfccGoal.levels.push({
                id: levelID,
                start: currentTime,
                modules: []
            })
        }

        if (nextLessonId) {
            newData.activeLessonUrl = window.location.pathname + '#' + nextLessonId
        }

        const parentLevelID = lessonID.split('--').slice(0, 2).join('--')
        const parentLevel = getLevelByID(parentLevelID, newData)

        if (!parentLevel.modules) {
            parentLevel.modules = []
        }

        const moduleExists = parentLevel.modules.some((module) => module.id === moduleID)
        if (!moduleExists) {
            parentLevel.modules.push({
                id: moduleID,
                start: currentTime
            })
        }
        const sfccModule = getModuleByID(moduleID, newData)
        if (!sfccModule)
            return {
                success: false,
                errorMsg: `Module with ID ${moduleID} does not exist in SFCC Customer's Profile`
            }

        if (!sfccModule.lessons) {
            sfccModule.lessons = []
        }

        const completeTime = new Date().toISOString()

        if (sfccModule.lessons.some((lesson) => lesson.id === lessonID)) {
            return {success: true}
        }

        sfccModule.lessons.push({
            id: lessonID,
            complete: completeTime
        })

        trackItemCompleted({
            eventType: learningCenterEventNames.lessonProgressStatus,
            id: lessonID,
            completedTimestamp: completeTime
        })
        if (nextLessonId) {
            trackItemStarted({
                eventType: learningCenterEventNames.lessonProgressStatus,
                id: nextLessonId,
                startedTimestamp: completeTime
            })
        }

        updateGuestLearnCenterData(newData)
        return updateData(newData)
    }

    /**
     * Adds the complete date to the selected level
     *
     * @param {string} levelID - level ID
     * @param {object} data - learning center data to update
     * @param {string} completeTime - complete Date (ISO format)
     * @returns {void}
     */
    const completeLevel = (levelID, data, completeTime) => {
        const sfccLevel = getLevelByID(levelID, data)
        if (!sfccLevel) return

        sfccLevel.complete = completeTime

        trackItemCompleted({
            eventType: learningCenterEventNames.levelProgressStatus,
            id: levelID,
            completedTimestamp: completeTime
        })
    }

    /**
     * Adds the complete date to the selected goal
     *
     * @param {string} goalID - goal ID
     * @param {object} data - learning center data to update
     * @param {string} completeTime - complete Date (ISO format)
     * @returns {void}
     */
    const completeGoal = (goalID, data, completeTime) => {
        const sfccGoal = getGoalByID(goalID, data)
        if (!sfccGoal) return

        sfccGoal.complete = completeTime

        trackItemCompleted({
            eventType: learningCenterEventNames.goalProgressStatus,
            id: goalID,
            completedTimestamp: completeTime
        })
    }

    /**
     * Marks the goal as started (adds the start date)
     *
     * @param {string} goalID - goal ID
     * @returns {object | Promise} A result object or a Promise that resolves with the result of the updateData method.
     */
    const startGoal = async (goalID) => {
        const sfccGoal = getGoalByID(goalID, learningCenterData)
        let newData = {}
        if (!sfccGoal) {
            const {data: openGoalData} = await openGoal(goalID, true)
            newData = openGoalData
        } else {
            newData = structuredClone(learningCenterData)
        }

        newData.activeGoal = goalID

        const newSfccGoal = getGoalByID(goalID, newData)
        const startTime = new Date().toISOString()
        newSfccGoal.start = startTime

        trackItemStarted({
            eventType: learningCenterEventNames.goalProgressStatus,
            id: goalID,
            startedTimestamp: startTime
        })

        return updateData(newData)
    }

    /**
     * Marks the level as started (adds the start date)
     *
     * @param {string} goalID - goal ID
     * @param {object} data - new learning center data object
     * @returns {void}
     */

    const startLevel = (levelID, goalID, data) => {
        const sfccGoal = getGoalByID(goalID, data)
        if (!sfccGoal) return
        const startTime = new Date().toISOString()
        sfccGoal.levels.push({
            id: levelID,
            start: startTime,
            modules: []
        })

        trackItemStarted({
            eventType: learningCenterEventNames.levelProgressStatus,
            id: levelID,
            startedTimestamp: startTime
        })
    }

    const setActiveGoal = async (goalID) => {
        let newData = learningCenterData ? structuredClone(learningCenterData) : {goals: []}

        // ensure the goal exists in the structure; if not, "open" it locally
        if (!getGoalByID(goalID, newData)) {
            const {data: opened} = await openGoal(goalID, true)
            newData = opened ?? newData
        }

        // set the active goal
        newData.activeGoal = goalID

        // persist to SFCC profile
        return updateData(newData)
    }

    const setActiveLesson = async (lessonUrl) => {
        const newData = learningCenterData ? structuredClone(learningCenterData) : {goals: []}

        newData.activeLessonUrl = lessonUrl

        return updateData(newData)
    }

    /**
     * Pushes the selected module to the parent level and returns the updated learning center data
     *
     * @param {string} moduleID - module ID
     * @param {string} parentLevelID - ID of the parent level
     * @param {object} data - new learning center data
     * @param {string} url - module URL
     * @returns {object} An updated learning center data
     */
    const startModule = (moduleID, parentLevelID, data, url) => {
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data
        const newData = structuredClone(parsedData || learningCenterData)
        const startTime = new Date().toISOString()
        const parentLevel = getLevelByID(parentLevelID, newData)

        if (!parentLevel) {
            return {
                success: false,
                errorMsg: `Level with id ${parentLevelID} does not exist`
            }
        }

        if (!parentLevel.modules) {
            parentLevel.modules = []
        }

        const moduleExists = parentLevel.modules.some((module) => module.id === moduleID)
        if (moduleExists) {
            setActiveLesson(url)
            return {
                success: true,
                data: learningCenterData
            }
        }

        parentLevel.modules.push({
            id: moduleID,
            start: startTime
        })

        trackItemStarted({
            eventType: learningCenterEventNames.moduleProgressStatus,
            id: moduleID,
            startedTimestamp: startTime
        })

        newData.activeLessonUrl = url

        return updateData(newData)
    }

    /**
     * Pushes the selected module to the parent level and returns the updated learning center data
     *
     * @param {boolean} quizPassed - is quiz passed (true|false)
     * @param {string} quizID - quiz ID
     * @param {string} moduleID - module ID
     * @param {object} parentLevel - parent_level_ref.data (builder content)
     * @param {string} parentLevel.id - parent level ID
     * @param {Array} parentLevel.modules - parent level modules array
     * @param {object} parentGoal - parent_goal_ref.data (builder content)
     * @param {string} parentGoal.id - parent goal ID
     * @param {Array} parentGoal.modules - parent goal levels array
     * @returns {object} An updated learning center data
     */
    const updateQuiz = async (quizPassed, quizID, moduleID, parentLevel, parentGoal) => {
        let newData = structuredClone(learningCenterData)
        const submitTime = new Date().toISOString()
        const sfccModule = getModuleByID(moduleID, newData)
        if (!sfccModule)
            return {
                success: false,
                errorMsg: `Module with ID ${moduleID} does not exist in SFCC Customer's Profile`
            }

        let sfccQuiz = sfccModule.quiz
        if (!sfccQuiz) {
            sfccQuiz = sfccModule.quiz = {
                id: quizID,
                attempts: []
            }
        }

        sfccQuiz.attempts.push({
            pass: quizPassed ? 1 : 0,
            time: submitTime
        })

        if (quizPassed) {
            sfccQuiz.complete = submitTime
            sfccModule.complete = submitTime
        }

        trackQuizAttempt({
            quizName: quizID,
            quizResult: quizPassed ? 'Pass' : 'Fail',
            attemptTimestamp: submitTime
        })
        if (quizPassed) {
            trackItemCompleted({
                eventType: learningCenterEventNames.moduleProgressStatus,
                id: moduleID,
                completedTimestamp: submitTime
            })
        }

        const sfccLevel = getLevelByID(parentLevel.id, newData)
        const allModulesComplete = areAllModulesComplete(parentLevel.modules, sfccLevel.modules)
        let allLevelsComplete = false

        if (allModulesComplete) {
            completeLevel(parentLevel.id, newData, submitTime)
            const sfccGoal = getGoalByID(parentGoal.id, newData)
            allLevelsComplete = areAllLevelsComplete(parentGoal.levels, sfccGoal.levels)
        }

        if (allLevelsComplete) {
            completeGoal(parentGoal.id, newData, submitTime)
        }

        const response = await updateData(newData)

        return {
            isGoalComplete: allLevelsComplete,
            isLevelComplete: allModulesComplete,
            responseData: response
        }
    }

    return {
        openGoal,
        completeLesson,
        completeLevel,
        completeGoal,
        startGoal,
        startLevel,
        startModule,
        updateQuiz,
        setActiveGoal,
        setActiveLesson
    }
}

export default useLearningCenterMutation
